import { ExpenseRate } from "~/gql/ExpenseRate";
import { Project, RateUnit, TaskRate } from "~/gql/types";
import Employee from "~/graphql/Employee";
import { FieldChanges, RateAmountMatchResult } from "./types";

type HandleMatchRateAmountProps = {
  rate: ExpenseRate | TaskRate;
  employee: Employee | null;
  project: Project;
};

export const handleMatchRateAmount = (
  props: HandleMatchRateAmountProps
): RateAmountMatchResult => {
  const { rate, employee, project } = props;
  switch (rate.__typename) {
    case "TaskRate":
      if (!rate.usesEmployeeRate) {
        return {
          tag: "RateAmountMatched",
          matchingRateAmount: `${rate.billableAmount}`,
        };
      }

      if (!employee) {
        return { tag: "RateAmountMatched", matchingRateAmount: `${0}` };
      }

      return { tag: "EmployeeRateRequired" };

    case "PerDiemExpenseRate":
      if (rate.billableOverride != null) {
        return {
          tag: "RateAmountMatched",
          matchingRateAmount: `${rate.billableOverride}`,
        };
      }

      if (!project.location) {
        return { tag: "NoMatchFound" };
      }

      // for per diem rates, the matching rate amount is based on the
      // kind of per diem expense rate, so we include the matching rate's per diem kind
      return { tag: "PerDiemRateRequired", perDiemKind: rate.kind };

    case "AtvExpenseRate":
    case "MobileAllowanceExpenseRate":
      return {
        tag: "RateAmountMatched",
        matchingRateAmount: `${rate.billableAmount}`,
      };
    case "MileageExpenseRate":
      return {
        tag: "RateAmountMatched",
        matchingRateAmount: `${rate.billableAmountPerMile}`,
      };
    case "ReceiptValuedExpenseRate":
    case "FuelExpenseRate":
      return { tag: "RateAmountCleared" };
    default:
      return { tag: "NoMatchFound" };
  }
};

type HandleMatchUnitAndQuantityProps = {
  rate: ExpenseRate | TaskRate;
  previousRate: ExpenseRate | TaskRate | null;
};

// a selected rate sets the unit, and the quantity for rates that carry
// one. moving off an hourly rate onto a non-hourly one clears the quantity.
export const handleMatchUnitAndQuantity = (
  props: HandleMatchUnitAndQuantityProps
): Pick<FieldChanges, "unit" | "quantity"> => {
  const { rate, previousRate } = props;
  const unit = rate.unit as RateUnit;

  if (
    rate.__typename === "PerDiemExpenseRate" ||
    rate.__typename === "MobileAllowanceExpenseRate"
  ) {
    return { unit, quantity: rate.quantity };
  }

  if (previousRate?.unit === RateUnit.Hourly && unit !== RateUnit.Hourly) {
    return { unit, quantity: null };
  }

  return { unit };
};
